import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterSignup from "../components/NewsletterSignup";
import EmailRoutingHero from "../components/EmailRoutingHero";
import PricingCards from "../components/PricingCards";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Globe2, Mail, ShieldCheck, Zap, Layers, Inbox, Users, Sparkles, ThumbsUp, ThumbsDown, Wrench, Leaf, Hammer, Paintbrush, Lock } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const FREE_PROVIDERS = ["@gmail.com", "@yahoo.com", "@outlook.com", "@aol.com", "@icloud.com", "@hotmail.com"];

const STEPS = [
  { num: "01", title: "Add your domain", body: "Point your domain (any registrar) at oly Inbox. We auto-detect your DNS provider — Dynadot, Cloudflare, Namecheap, GoDaddy — and walk you through the exact 2 records to add.", icon: Globe2 },
  { num: "02", title: "Create your email", body: "Pick the alias that makes you look professional — hello@, info@, or matthew@. Choose where it forwards (your existing Gmail, Yahoo, Outlook, or iCloud inbox).", icon: Mail },
  { num: "03", title: "Reply from your inbox", body: "Use your inbox's built-in 'Send mail as' (Gmail, Yahoo, Outlook, iCloud all support it) to reply from your domain — your customer never sees your personal address. Done in 3 minutes.", icon: Inbox },
];

const FAQS = [
  { q: "Why not just use Google Workspace?", a: "Workspace is great if you want a whole new inbox and admin panel — but it's $6+/user/month and you have to migrate. oly Inbox keeps you in your existing inbox and starts at $3/month." },
  { q: "How is spam handled?", a: "We deliberately don't include catch-all forwarding (which is the #1 spam vector). Every alias is explicit, verified, and rate-limited. SPF, DKIM and SRS are set up for you." },
  { q: "Can I send emails from my domain?", a: "Yes — included in every plan. We provision SMTP credentials you paste once into your inbox's 'Send mail as' setting (Gmail, Yahoo, Outlook, iCloud all support this). Replies land in your normal inbox and go out from hello@yourdomain.com." },
  { q: "What if I don't own a domain yet?", a: "We partner with Dynadot — transparent pricing, free WHOIS privacy, and no upsell ladder. Once purchased, come back and we'll wire it up automatically." },
  { q: "Can I add more than one domain?", a: "Yes. Every plan links 1 domain. Need to manage a second (or third) domain from the same dashboard? Add extra domain slots for just $3/mo each — no need to create a new account." },
  { q: "Are there email limits?", a: "Yes — designed to keep your messages out of spam folders. Starter: 200/day inbound + 50/day outbound. Growth: 1,000/250. Pro: 5,000/1,000. Limits are well above what 99% of small businesses need." },
  { q: "Can I cancel anytime?", a: "Anytime, no questions asked. Annual plans are prorated for refunds in the first 30 days." },
];

const PROVIDERS = ["Dynadot", "Cloudflare", "Namecheap", "GoDaddy", "Google Domains", "Porkbun", "Squarespace"];

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(0);
  const { config, user, refresh } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const buyDomainUrl = config?.registrar?.affiliate_url || config?.spaceship_affiliate_url || "https://www.dynadot.com/?rsc=olyinbox";
  const registrarName = config?.registrar?.name || "Dynadot";

  // Auto-scroll to hash target (e.g. /#pricing from "Change plan" in Dashboard)
  useEffect(() => {
    if (loc.hash) {
      const id = loc.hash.replace("#", "");
      // Wait a tick for DOM to render, then smooth-scroll
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [loc.hash]);

  // Clover App Market redirect handoff:
  //   /?code=<auth_code>&merchant_id=<mid>  (and optionally &client_id=...)
  // Exchange the code, then either auto-login or stash a pending_clover_token
  // for the /signup page to consume.
  const cloverExchangedRef = useRef(null);
  useEffect(() => {
    const sp = new URLSearchParams(loc.search);
    const code = sp.get("code");
    const merchantId = sp.get("merchant_id");
    if (!code || !merchantId) return;
    // Idempotency guard — React StrictMode double-mounts effects in dev,
    // and we never want to fire the OAuth exchange twice for the same code.
    const key = `${code}:${merchantId}`;
    if (cloverExchangedRef.current === key) return;
    cloverExchangedRef.current = key;

    // Clean OAuth params from the URL immediately so refreshes/back-button
    // don't re-trigger and the code never ends up in an external referer.
    if (typeof window !== "undefined") {
      const cleanUrl = window.location.pathname + (loc.hash || "");
      window.history.replaceState({}, "", cleanUrl);
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/clover/oauth/exchange", {
          code,
          merchant_id: merchantId,
        });
        if (cancelled) return;
        if (data?.status === "logged_in") {
          // Cookie was set server-side via session token; refresh user state.
          await refresh();
          toast.success("Welcome back — Clover linked.");
          nav("/dashboard", { replace: true });
        } else if (data?.status === "needs_registration" && data.pending_token) {
          window.sessionStorage.setItem("pending_clover_token", data.pending_token);
          window.sessionStorage.setItem("clover_merchant_id", data.merchant_id || merchantId);
          if (data.merchant_name) {
            window.sessionStorage.setItem("clover_merchant_name", data.merchant_name);
          }
          toast.success(
            data.merchant_name
              ? `Linking ${data.merchant_name} on Clover — finish creating your account.`
              : "Clover detected — finish creating your account."
          );
          nav("/signup", { replace: true });
        }
      } catch (e) {
        toast.error(formatApiError(e) || "Couldn't complete Clover sign-in.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  return (
    <div className="min-h-screen flex flex-col oly-grid-bg">
      <Header />

      {/* Top banner: domain-less nudge */}
      <div className="bg-[#0F172A] text-white" data-testid="top-banner">
        <Link
          to={user && typeof user === "object" ? "/dashboard" : "/signup"}
          className="max-w-7xl mx-auto px-6 lg:px-8 py-2.5 flex items-center justify-center gap-2 text-sm font-medium hover:bg-[#1E293B] transition-colors"
        >
          <Sparkles className="h-3.5 w-3.5 text-[#F26953]" />
          <span className="text-slate-200">Don't have a domain yet?</span>
          <span className="text-white">We'll help you find one inside</span>
          <ArrowRight className="h-3.5 w-3.5 text-[#F26953]" />
        </Link>
      </div>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 md:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-7 animate-float-in">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FEE7E1] px-3 py-1 text-xs font-semibold text-[#F26953]">
              <Sparkles className="h-3 w-3"/> From the team behind <a href="https://olypayroll.com" target="_blank" rel="noreferrer" className="underline">oly Payroll</a>
            </div>
            <h1 className="text-5xl md:text-6xl tracking-tight font-bold text-[#0F172A] leading-[1.05]">
              Professionalize your email in <span className="text-[#F26953]">under 3 minutes</span>.
            </h1>
            <p className="text-lg text-[#475569] max-w-xl leading-relaxed">
              Your customers judge your business by your email address. Replace
              <span className="mono text-sm bg-slate-100 px-1.5 py-0.5 rounded"> yourname@gmail.com</span>
              with
              <span className="mono text-sm bg-[#FEE7E1] text-[#F26953] px-1.5 py-0.5 rounded"> hello@yourdomain.com</span>
              — forwarded straight to the inbox you already check. <strong className="text-[#0F172A]">No migration. No new app. No IT help.</strong>
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={user && typeof user === "object" ? "/dashboard" : "/signup"} data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-[#D85340] transition-colors shadow-sm">
                Set up my email <ArrowRight className="h-4 w-4"/>
              </Link>
              <a href="#how" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white text-[#0F172A] border border-[#E2E8F0] font-semibold px-6 py-3.5 rounded-lg hover:bg-[#F8FAFC]">
                See how it works
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-[#475569] pt-2">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-500"/> SPF + DKIM included</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-[#F26953]"/> 3-min setup</span>
              <span>From <strong className="text-[#0F172A]">$3/mo</strong></span>
            </div>
            <div
              data-testid="hero-privacy-reassurance"
              className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-emerald-50 border border-emerald-200 px-3.5 py-2 text-xs sm:text-sm text-emerald-800"
            >
              <Lock className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span><strong className="font-semibold">Your email stays yours.</strong> We forward mail in real time — nothing is kept on our servers.</span>
            </div>
          </div>
          <div className="relative">
            <EmailRoutingHero />
          </div>
        </div>

        {/* Trust strip */}
        <div className="border-y border-[#E2E8F0] bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Works with every registrar</span>
            {PROVIDERS.map(p => (
              <span key={p} className="text-sm font-semibold text-slate-500">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* MID CTA — "Stop emailing from @gmail / @yahoo / @outlook / @aol" */}
      <section className="py-20 md:py-28 bg-white border-b border-[#E2E8F0]" data-testid="mid-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl md:text-6xl font-bold text-[#0F172A] tracking-tight leading-[1.1]">
            Stop emailing customers from a free inbox.
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2" data-testid="free-providers-list">
            {FREE_PROVIDERS.map((p) => (
              <span
                key={p}
                data-testid={`free-provider-${p.slice(1)}`}
                className="mono text-base md:text-lg text-slate-400 line-through decoration-2 decoration-[#F26953]/60"
              >
                {p}
              </span>
            ))}
          </div>
          <p className="text-lg md:text-xl text-[#475569] max-w-2xl mx-auto">
            Three minutes from now you could have professional email at your domain — routed straight to the inbox you already use.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to={user && typeof user === "object" ? "/dashboard" : "/signup"}
              data-testid="mid-cta-primary"
              className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-7 py-4 rounded-lg hover:bg-[#D85340] shadow-md transition-colors"
            >
              Set up my email — $3/mo <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href="#pricing"
              data-testid="mid-cta-secondary"
              className="inline-flex items-center gap-2 bg-white text-[#0F172A] border border-[#E2E8F0] font-semibold px-7 py-4 rounded-lg hover:bg-slate-50"
            >
              See pricing
            </a>
          </div>
        </div>
      </section>

      {/* TRADES COMPARISON — "Which one looks like a real business?" */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="trades-comparison">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#F26953]">For real businesses</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] mt-3 tracking-tight">
              Which one looks like a real business?
            </h2>
            <p className="text-lg text-[#475569] mt-4">
              You're a plumber, a gardener, a painter, a cleaner — not a web developer. You just want to look like the pro you already are. Customers decide in under 10 seconds whether to trust your quote. Here's what they see:
            </p>
          </div>

          {/* Side-by-side fake emails */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-start">
            {/* BEFORE — free inbox */}
            <div className="relative" data-testid="email-before">
              <div className="absolute -top-4 left-6 z-10 inline-flex items-center gap-1.5 bg-slate-200 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-full">
                <ThumbsDown className="h-3 w-3"/> Looks like a side hustle
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden opacity-95">
                <div className="px-5 py-4 bg-slate-100 border-b border-slate-200 flex items-center gap-2 text-xs text-slate-500">
                  <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300"/>
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300"/>
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300"/>
                  </div>
                  <span className="ml-2">New message</span>
                </div>
                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-slate-200 grid place-items-center text-slate-500 text-xs font-bold">JP</div>
                    <div className="min-w-0 flex-1">
                      <div className="mono text-sm text-slate-600 truncate">joeysplumbing<span className="text-slate-400">@aol.com</span></div>
                      <div className="text-[11px] text-slate-400">to: you@example.com</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-700">RE: quote for bathroom</div>
                  <div className="text-sm text-slate-500 leading-relaxed">
                    hey — here's the quote for ur bathroom. $2400 total. lmk
                    <br/><br/>
                    - joey
                  </div>
                </div>
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-400 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-400"/>
                  Customer thought: <span className="italic">"Is this guy even a real business?"</span>
                </div>
              </div>
            </div>

            {/* AFTER — domain email */}
            <div className="relative md:mt-8" data-testid="email-after">
              <div className="absolute -top-4 left-6 z-10 inline-flex items-center gap-1.5 bg-[#F26953] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                <ThumbsUp className="h-3 w-3"/> Looks like a real business
              </div>
              <div className="rounded-2xl bg-white border-2 border-[#F26953]/30 shadow-xl overflow-hidden">
                <div className="px-5 py-4 bg-gradient-to-r from-[#FEE7E1] to-white border-b border-[#F26953]/20 flex items-center gap-2 text-xs text-[#0F172A]">
                  <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#F26953]"/>
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400"/>
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400"/>
                  </div>
                  <span className="ml-2 font-semibold">New message</span>
                </div>
                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#F26953] grid place-items-center text-white text-xs font-bold">JP</div>
                    <div className="min-w-0 flex-1">
                      <div className="mono text-sm text-[#0F172A] font-semibold truncate">info<span className="text-[#F26953]">@joeysplumbing.com</span></div>
                      <div className="text-[11px] text-slate-400">to: you@example.com</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-[#0F172A]">Your quote from Joey's Plumbing</div>
                  <div className="text-sm text-[#475569] leading-relaxed">
                    Hi — thanks for having us over on Thursday. Attached is your estimate for the bathroom rough-in. Total: <strong className="text-[#0F172A]">$2,400</strong>, valid for 30 days.
                    <br/><br/>
                    Happy to answer any questions.
                    <br/><br/>
                    Joey Martinez<br/>
                    <span className="text-slate-400">Licensed Master Plumber · Joey's Plumbing LLC</span>
                  </div>
                </div>
                <div className="px-6 py-3 bg-[#FEE7E1]/40 border-t border-[#F26953]/20 text-[11px] text-[#0F172A] flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>
                  Customer thought: <span className="italic">"These folks look legit. Let's book."</span>
                </div>
              </div>
            </div>
          </div>

          {/* More examples */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="trade-examples">
            {[
              { icon: Wrench, biz: "Joey's Plumbing", old: "joeysplumbing@aol.com", new: "info@joeysplumbing.com" },
              { icon: Leaf, biz: "Green Thumb Landscaping", old: "greenthumb2011@yahoo.com", new: "hello@greenthumb.co" },
              { icon: Hammer, biz: "MK Construction", old: "mkconstruction@yahoo.com", new: "michael@mkconstruction.com" },
              { icon: Paintbrush, biz: "Carmen's Cleaning", old: "carmencleans@gmail.com", new: "bookings@carmensclean.com" },
            ].map(({ icon: Icon, biz, old, new: nu }) => (
              <div key={biz} className="rounded-xl bg-white border border-[#E2E8F0] p-5 space-y-3" data-testid={`example-${biz.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-[#FEE7E1] grid place-items-center">
                    <Icon className="h-4 w-4 text-[#F26953]"/>
                  </div>
                  <span className="text-sm font-bold text-[#0F172A]">{biz}</span>
                </div>
                <div className="space-y-1.5">
                  <div className="mono text-xs text-slate-400 line-through decoration-slate-300">{old}</div>
                  <div className="mono text-xs text-[#0F172A] font-semibold flex items-start gap-1">
                    <ArrowRight className="h-3 w-3 text-[#F26953] flex-shrink-0 mt-0.5"/>
                    {nu}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 max-w-2xl mx-auto text-center">
            <p className="text-base text-[#475569]">
              You're not changing how you do the work. You're just making sure the first impression matches the quality you deliver.
            </p>
            <Link to={user && typeof user === "object" ? "/dashboard" : "/signup"}
              data-testid="trades-cta"
              className="mt-6 inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#D85340] shadow-sm">
              Get my business email — $3/mo <ArrowRight className="h-4 w-4"/>
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#F26953]">How it works</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] mt-3 tracking-tight">Three steps. Five minutes. Done forever.</h2>
            <p className="text-lg text-[#475569] mt-4">No new inbox to log into. No team training. Just professional email forwarded to where you already check it.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.num} data-testid={`how-step-${s.num}`} className="rounded-2xl bg-white border border-[#E2E8F0] p-8 shadow-sm hover:shadow-md transition-shadow group">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-[Outfit] font-black text-4xl text-[#FEE7E1] group-hover:text-[#F26953] transition-colors">{s.num}</span>
                    <div className="h-11 w-11 rounded-xl bg-[#0F172A] grid place-items-center">
                      <Icon className="h-5 w-5 text-white"/>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A]">{s.title}</h3>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { icon: ShieldCheck, k: "Anti-spam by design", v: "No catch-all forwarding. Every alias is intentional. SPF, DKIM, and SRS are configured for you so your messages don't get flagged as junk." },
            { icon: Layers, k: "Cheaper than Workspace", v: "Starts at $3/mo — half the price of Google Workspace, with none of the migration headache. Use the inbox you already use." },
            { icon: Users, k: "Grows with your team", v: "Add aliases as you hire — info@, support@, orders@. Add extra domain slots in one click for just $3/mo each." },
          ].map(({ icon: I, k, v }) => (
            <div key={k} className="space-y-3" data-testid={`value-${k.toLowerCase().replace(/\s+/g, "-")}`}>
              <I className="h-6 w-6 text-[#F26953]"/>
              <h3 className="text-xl font-bold font-[Outfit]">{k}</h3>
              <p className="text-sm text-slate-300 leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#F26953]">Pricing</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] mt-3 tracking-tight">Simple plans. No nonsense.</h2>
            <p className="text-lg text-[#475569] mt-4">Pick a plan, link your domain, and you'll have professional email by the end of this coffee. Need a second domain later? Add it for $3/mo from your dashboard — no new account required.</p>
          </div>
          <PricingCards />
        </div>
      </section>

      {/* DOMAIN UPSELL */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#FEE7E1] to-white border border-[#F26953]/20 p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#F26953]">Don't have a domain yet?</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight">Get one in 60 seconds with {registrarName}.</h2>
              <p className="text-[#475569]">Transparent wholesale-style pricing, free WHOIS privacy, no upsell carousels, no domain renewal surprises. Come back here once it's purchased and we'll wire up your email instantly.</p>
            </div>
            <div className="flex md:justify-end">
              <a href={buyDomainUrl} target="_blank" rel="noreferrer" data-testid="buy-domain-cta"
                className="inline-flex items-center gap-2 bg-[#0F172A] text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-slate-800 shadow-sm">
                Browse domains on {registrarName} <ArrowRight className="h-4 w-4"/>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 bg-white border-t border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#F26953]">FAQ</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] mt-3 tracking-tight">Common questions</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors">
                  <span className="font-semibold text-[#0F172A]">{f.q}</span>
                  <span className="text-2xl text-[#F26953] font-light">{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-[#475569] leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight">
            Ready when you are.
          </h2>
          <p className="text-lg text-[#475569]">Link your domain, pick an alias, and you're done. Seriously, that's it.</p>
          <Link to={user && typeof user === "object" ? "/dashboard" : "/signup"} data-testid="final-cta"
            className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#D85340] shadow-md">
            Get started — $3/mo <ArrowRight className="h-5 w-5"/>
          </Link>
        </div>
      </section>

      <NewsletterSignup />

      <Footer />
    </div>
  );
}